import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Check, Shield, Lock, Eye, Zap, Settings, Users, Ban, UserCheck, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

const ResidentialPricingSection = () => {
  const [isIPMaskDialogOpen, setIsIPMaskDialogOpen] = useState(false);
  const [isProxyPanelDialogOpen, setIsProxyPanelDialogOpen] = useState(false);
  const [isATNetDialogOpen, setIsATNetDialogOpen] = useState(false);
  const [showATNetForm, setShowATNetForm] = useState(false);
  const [hoveredATNet, setHoveredATNet] = useState(false);

  const pricingPlans = [
    {
      name: 'Natively Routed',
      pricePerIP: '$1.50',
      brandName: 'WinRoute™',
      features: ['IPMask™ Protected', 'Skilled Technical Support', 'Deployed via ProxyPanel™', 'Basic Geo-tagging', '99.9% Uptime'],
      popular: false,
      colorScheme: 'teal', // WindStream-inspired
      planUrl: 'https://acc.nexx.net/index.php?rp=/store/winroute-proxies'
    },
    {
      name: 'Native Pool',
      pricePerIP: '$2.00',
      brandName: 'VRed™',
      features: ['IPMask™ Protected', 'Skilled Technical Support', 'Deployed via ProxyPanel™', 'Basic Geo-tagging', '99.9% Uptime'],
      popular: true,
      colorScheme: 'verizon', // Verizon-inspired
      planUrl: 'https://acc.nexx.net/index.php?rp=/store/vred-proxies'
    },
    {
      name: 'Native Pool',
      pricePerIP: '$3.00',
      brandName: 'ATNet™',
      features: ['IPMask™ Protected', 'Skilled Technical Support', 'Deployed via ProxyPanel™', 'Basic Geo-tagging', '99.9% Uptime'],
      popular: false,
      colorScheme: 'att' // AT&T-inspired
    }
  ];

  const getColorClasses = (colorScheme: string, popular: boolean) => {
    switch (colorScheme) {
      case 'teal':
        return {
          border: popular ? 'border-2 border-teal-500 shadow-xl ring-4 ring-teal-500/10' : 'border border-teal-200 shadow-lg hover:shadow-xl',
          badge: 'bg-teal-500 text-white',
          brandBadge: 'bg-teal-600 text-white border-teal-700',
          price: 'text-teal-600',
          checkIcon: 'text-teal-500',
          button: popular ? 'bg-teal-500 hover:bg-teal-600 text-white' : 'border-teal-500 text-teal-600 hover:bg-teal-500 hover:text-white',
          gradient: 'from-teal-50 to-emerald-50'
        };
      case 'verizon':
        return {
          border: popular ? 'border-2 border-red-600 shadow-xl ring-4 ring-red-600/10' : 'border border-red-200 shadow-lg hover:shadow-xl',
          badge: 'bg-red-600 text-white',
          brandBadge: 'bg-red-600 text-white border-red-700',
          price: 'text-red-600',
          checkIcon: 'text-red-600',
          button: popular ? 'bg-red-600 hover:bg-red-700 text-white' : 'border-red-600 text-red-600 hover:bg-red-600 hover:text-white',
          gradient: 'from-gray-50 to-red-50'
        };
      case 'att':
        return {
          border: popular ? 'border-2 border-blue-600 shadow-xl ring-4 ring-blue-600/10' : 'border border-blue-200 shadow-lg hover:shadow-xl',
          badge: 'bg-blue-600 text-white',
          brandBadge: 'bg-blue-600 text-white border-blue-700',
          price: 'text-blue-600',
          checkIcon: 'text-blue-600',
          button: popular ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white',
          gradient: 'from-blue-50 to-gray-50'
        };
      default:
        return {
          border: popular ? 'border-2 border-nexx-green shadow-xl ring-4 ring-nexx-green/10' : 'border border-gray-200 shadow-lg hover:shadow-xl',
          badge: 'bg-nexx-green text-white',
          brandBadge: 'bg-nexx-green text-white border-green-700',
          price: 'text-nexx-green',
          checkIcon: 'text-nexx-green',
          button: popular ? 'bg-nexx-green hover:bg-green-700 text-white' : 'border-nexx-green text-nexx-green hover:bg-nexx-green hover:text-white',
          gradient: 'from-gray-50 to-white'
        };
    }
  };

  const renderFeature = (feature: string, index: number, colors: any) => {
    if (feature === 'IPMask™ Protected') {
      return (
        <li key={index} className="flex items-center text-gray-600">
          <Check className={`h-5 w-5 ${colors.checkIcon} mr-3 flex-shrink-0`} />
          <Dialog open={isIPMaskDialogOpen} onOpenChange={setIsIPMaskDialogOpen}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 hover:text-gray-800 transition-colors">
                <Badge variant="outline" className="cursor-pointer hover:bg-gray-50">
                  {feature}
                </Badge>
                <span className="text-xs text-blue-600 hover:underline">learn more</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-2xl">
                  <Shield className="h-6 w-6 text-blue-600" />
                  What Is IP Mask Protection?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 text-gray-700">
                <p className="text-lg">
                  Every IP address in our network is IP Mask Protected—giving you an extra layer of security and stability.
                </p>
                <p>
                  The IP Mask System works silently in the background, placing your traffic behind advanced protection protocols that make it harder to detect, disrupt, or abuse. It's built into all proxy sessions and optimized for performance.
                </p>
                
                <div className="border-t border-gray-200 my-6"></div>
                
                <div>
                  <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                    <Lock className="h-5 w-5 text-green-600" />
                    How IP Mask Works for You:
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <Shield className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Firewall-Level Cloaking</h4>
                        <p className="text-sm text-gray-600">Your proxy sessions are routed through our secured infrastructure, hiding them from common scanning and tracing tools.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Lock className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Proactive Abuse Filtering</h4>
                        <p className="text-sm text-gray-600">We block abusive traffic patterns before they ever reach you, reducing the risk of bans or platform detection.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Eye className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Real-Time Anomaly Detection</h4>
                        <p className="text-sm text-gray-600">Our system constantly watches for suspicious activity and adjusts dynamically to keep your sessions clean.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Zap className="h-5 w-5 text-yellow-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Reduced Flagging & Churn</h4>
                        <p className="text-sm text-gray-600">By maintaining a cleaner reputation across all IPs, you get longer-lasting sessions with fewer interruptions.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 my-6"></div>
                
                <p className="text-center text-gray-800 font-medium">
                  You don't have to configure anything—IP Mask Protection is always on. It's part of what makes our proxy network one of the most trusted in the space.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </li>
      );
    }

    if (feature === 'Deployed via ProxyPanel™') {
      return (
        <li key={index} className="flex items-center text-gray-600">
          <Check className={`h-5 w-5 ${colors.checkIcon} mr-3 flex-shrink-0`} />
          <Dialog open={isProxyPanelDialogOpen} onOpenChange={setIsProxyPanelDialogOpen}>
            <DialogTrigger asChild>
              <button className="flex items-center gap-2 hover:text-gray-800 transition-colors">
                <Badge variant="outline" className="cursor-pointer hover:bg-gray-50">
                  {feature}
                </Badge>
                <span className="text-xs text-blue-600 hover:underline">learn more</span>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-2xl">
                  <Settings className="h-6 w-6 text-blue-600" />
                  What Is Proxy Panel?
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 text-gray-700">
                <p className="text-lg">
                  Proxy Panel is the command center for managing and deploying your proxy infrastructure—simple, powerful, and built for scale.
                </p>
                <p>
                  Whether you're reselling, restricting access, or just staying in control of your resources, Proxy Panel gives you everything you need to operate efficiently and securely.
                </p>
                
                <div className="border-t border-gray-200 my-6"></div>
                
                <div>
                  <h3 className="flex items-center gap-2 text-xl font-semibold mb-4">
                    <Settings className="h-5 w-5 text-green-600" />
                    What Proxy Panel Lets You Do:
                  </h3>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <UserCheck className="h-5 w-5 text-blue-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">User/Password Authentication</h4>
                        <p className="text-sm text-gray-600">Set up credential-based access to proxies for increased control and traceability.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Shield className="h-5 w-5 text-green-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">IP Block Reselling</h4>
                        <p className="text-sm text-gray-600">Create, assign, and manage proxy users from your own pool—perfect for resellers and enterprise teams.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Ban className="h-5 w-5 text-red-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Website Blocking Controls</h4>
                        <p className="text-sm text-gray-600">Block access to specific websites across your proxy network using our integrated blacklist system.</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <Users className="h-5 w-5 text-purple-500 mt-1 flex-shrink-0" />
                      <div>
                        <h4 className="font-semibold">Simple User Management (Optional addition)</h4>
                        <p className="text-sm text-gray-600">Add, suspend, or delete users with just a few clicks—no command-line needed.</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 my-6"></div>
                
                <p className="text-center text-gray-800 font-medium">
                  Included with every deployment. No extra fees, no steep learning curve—just powerful control where you need it most.
                </p>
              </div>
            </DialogContent>
          </Dialog>
        </li>
      );
    }

    return (
      <li key={index} className="flex items-center text-gray-600">
        <Check className={`h-5 w-5 ${colors.checkIcon} mr-3 flex-shrink-0`} />
        {feature}
      </li>
    );
  };

  const handleATNetContinue = () => {
    setShowATNetForm(true);
  };

  const handleATNetDialogClose = () => {
    setIsATNetDialogOpen(false);
    setShowATNetForm(false);
  };

  const handleATNetSeeePlans = () => {
    setIsATNetDialogOpen(true);
  };

  const handlePlanClick = (plan: any) => {
    if (plan.brandName === 'ATNet™') {
      handleATNetSeeePlans();
    } else if (plan.planUrl) {
      window.open(plan.planUrl, '_blank');
    }
  };

  return (
    <section id="pricing-section" className="py-16 bg-white relative section-drift">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Simple Pricing. Scale As You Grow.
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transparent rates. No bandwidth throttling. Pay only for what you use.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {pricingPlans.map((plan, index) => {
            const colors = getColorClasses(plan.colorScheme, plan.popular);
            const isATNet = plan.brandName === 'ATNet™';
            
            return (
              <div
                key={plan.name}
                className={`relative bg-gradient-to-br ${colors.gradient} rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 animate-fade-in ${colors.border}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className={`${colors.badge} px-4 py-1 rounded-full text-sm font-medium`}>
                      Most Popular
                    </div>
                  </div>
                )}
                
                {/* Brand Badge */}
                <div className="absolute top-4 right-4">
                  <div className={`${colors.brandBadge} px-3 py-1 rounded-lg text-xs font-bold border-2 shadow-sm`}>
                    {plan.brandName}
                  </div>
                </div>
                
                <div className="text-center mb-6 pt-4">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="text-sm text-gray-500 mb-4">Per IP Address</div>
                  <div className={`text-4xl font-bold ${colors.price}`}>
                    {plan.pricePerIP}
                    <span className="text-lg text-gray-500">/IP</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => 
                    renderFeature(feature, featureIndex, colors)
                  )}
                </ul>

                {isATNet ? (
                  <div className="relative">
                    <div 
                      className={`w-full overflow-hidden rounded-md transition-all duration-300 ease-in-out ${hoveredATNet ? 'h-20' : 'h-10'}`}
                      onMouseEnter={() => setHoveredATNet(true)}
                      onMouseLeave={() => setHoveredATNet(false)}
                    >
                      {/* Top section - See Plans */}
                      <div 
                        className={`w-full h-10 flex items-center justify-center cursor-pointer transition-all duration-300 ${colors.button} ${plan.popular ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white'} border rounded-t-md ${hoveredATNet ? 'rounded-b-none' : 'rounded-b-md'}`}
                        onClick={handleATNetSeeePlans}
                      >
                        <span className="text-sm font-medium">See Plans</span>
                      </div>
                      
                      {/* Bottom section - Apply To Purchase */}
                      <div 
                        className={`w-full h-10 flex items-center justify-center cursor-pointer bg-red-600 hover:bg-red-700 text-white transition-all duration-300 rounded-b-md ${hoveredATNet ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}
                        onClick={handleATNetSeeePlans}
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        <span className="text-sm font-medium">Apply To Purchase</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Button 
                    className={`w-full ${colors.button}`}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => handlePlanClick(plan)}
                  >
                    See Plans
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* ATNet Application Dialog */}
        <Dialog open={isATNetDialogOpen} onOpenChange={handleATNetDialogClose}>
          <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] p-0 overflow-hidden">
            <DialogTitle className="sr-only">ATNet Application Process</DialogTitle>
            <div className="w-full h-[85vh] p-6">
              {!showATNetForm ? (
                <div className="space-y-6 text-gray-700 max-w-3xl mx-auto">
                  <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <AlertTriangle className="h-8 w-8 text-red-600" />
                      <h2 className="text-2xl font-bold text-gray-900">Apply to Access AT&T-Backed Proxy Routes</h2>
                    </div>
                  </div>
                  
                  <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
                    <p className="text-lg leading-relaxed">
                      Due to high demand and the sensitive nature of proxy services that leverage AT&T network pathways, access is limited to approved clients only. In line with our commitment to responsible use and network integrity, all requests undergo a brief screening process.
                    </p>
                  </div>
                  
                  <p className="text-lg">
                    NEXX TELECOM is focused on delivering secure, high-quality connectivity solutions. As part of that commitment, we carefully review all applications involving AT&T-routed infrastructure.
                  </p>
                  
                  <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                    <h3 className="flex items-center gap-2 text-xl font-semibold mb-4 text-green-800">
                      <Check className="h-5 w-5" />
                      Why Approval Is Required:
                    </h3>
                    <ul className="space-y-3 text-green-700">
                      <li className="flex gap-3">
                        <span className="text-green-600">•</span>
                        <span>These routes are in exceptionally high demand</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="text-green-600">•</span>
                        <span>We maintain a compliance-first framework across all services</span>
                      </li>
                      <li className="flex gap-3">
                        <span className="text-green-600">•</span>
                        <span>Our priority is long-term availability for qualified partners</span>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="text-center pt-6">
                    <p className="text-gray-600 mb-6">
                      Click "Continue" to begin your application. A member of our team will review your submission and follow up within 24–48 hours.
                    </p>
                    <Button 
                      onClick={handleATNetContinue}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full">
                  <div className="mb-4 text-center">
                    <h3 className="text-xl font-semibold text-gray-900">AT&T Network Access Application</h3>
                    <p className="text-gray-600">Please complete the form below to apply for access</p>
                  </div>
                  <iframe 
                    src="https://tally.so/embed/wd4EOr?alignLeft=1&hideTitle=1&transparentBackground=1&dynamicHeight=1"
                    loading="lazy" 
                    width="100%" 
                    height="566"
                    style={{ border: 'none', borderRadius: '8px' }}
                    title="AT&T KYC"
                    className="w-full h-full rounded-lg"
                  />
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <div className="text-center">
          <p className="text-gray-600 text-lg">
            All plans include <span className="font-semibold text-nexx-green">24/7 support</span> and <span className="font-semibold text-nexx-green">instant activation</span>.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ResidentialPricingSection;
